import { useFormik } from 'formik';
import { get } from 'lodash';
import GenericSelect from '../../../../components/GenericSelect';
import { FormulaService } from '../../../../components/search/formulaService';
import { Box } from '../../../../components/UI-CHAKRA/Box';
import { Button } from '../../../../components/UI-CHAKRA/Button';
import { Flex } from '../../../../components/UI-CHAKRA/Flex';
import { FormControl } from '../../../../components/UI-CHAKRA/FormControl';
import { FormErrorMessage } from '../../../../components/UI-CHAKRA/FormErrorMessage';
import { FormLabel } from '../../../../components/UI-CHAKRA/FormLabel';
import { Grid } from '../../../../components/UI-CHAKRA/Grid';
import { GridItem } from '../../../../components/UI-CHAKRA/GridItem';
import { Input } from '../../../../components/UI-CHAKRA/Input/Input';
import { Text } from '../../../../components/UI-CHAKRA/Text';
import Textarea from '../../../../components/UI-CHAKRA/Textarea';
import { toast } from '../../../../helpers/toast';
import { Yup } from '../../../../helpers/Yup';
import { Formula } from '../../../../interfaces/formula';
import { MEDICINE_TYPE_OPTIONS_MAP_FORMULA, PRESCRIPTION_TYPE } from '../../../../interfaces/medicine';
import { usePrescriptionStore } from '../../../../stores/prescriptionStore';
import { amplitudeModule } from '../../../../shared/amplitude';
import { PrescriptionService } from '../../service';
import LazyLoadingSelect from './LazyLoadingSelect';

interface FormulaFormDialogProps {
  onClose: () => void;
  selectedFormula: Formula | null;
  isEditingFormula: boolean;
  refreshFormulas: () => void;
  handleSetIsEditingFormula: (isEditing: boolean) => void;
}

interface FormulaFormValues {
  formulaName: string;
  prescriptionType: PRESCRIPTION_TYPE | '';
  physicalForm: string | '';
  quantity: number | '';
  measure: string | '';
  composition: string;
  posology: string;
}

const FormulaFormDialog = (props: FormulaFormDialogProps) => {
  const { onClose, selectedFormula, isEditingFormula } = props;
  const prescription = usePrescriptionStore((store) => store.prescription);
  const prescriber = usePrescriptionStore((store) => store.prescriber());
  const setPrescription = usePrescriptionStore((store) => store.setPrescription);
  const prescriberConfiguration = usePrescriptionStore((store) => store.prescriberConfiguration);

  const isAddingFromSearch = !!(selectedFormula && !isEditingFormula);

  const prescriptionId = prescription?.idPrescricao || null;
  const doctorId = prescriber?.idPrescritor || null;

  const validationSchema = Yup.object().shape({
    formulaName: Yup.string().required('Indique qual o nome da fórmula').trim(),
    prescriptionType: Yup.string().required('Selecione o tipo de receituário'),
    physicalForm: Yup.string().required('Selecione a forma física').trim(),
    quantity: Yup.number()
      .required('Quantidade é obrigatória')
      .min(1, 'Quantidade deve ser maior que zero')
      .typeError('Quantidade deve ser um número válido'),
    measure: Yup.string().required('Selecione a Medida').trim(),
    composition: Yup.string().required('Insira uma composição').trim(),
    posology: Yup.string().required('Indique a posologia').trim(),
  });

  const formik = useFormik<FormulaFormValues>({
    initialValues: {
      formulaName: selectedFormula?.formulaName || '',
      prescriptionType: (selectedFormula?.prescriptionType as PRESCRIPTION_TYPE) || '',
      physicalForm: selectedFormula?.physicalForm || '',
      quantity: selectedFormula?.quantity || '',
      measure: selectedFormula?.measure || '',
      composition: selectedFormula?.composition || '',
      posology: selectedFormula?.posology || '',
    },
    enableReinitialize: true,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (values) => {
      const fields: (keyof FormulaFormValues)[] = [
        'formulaName',
        'prescriptionType',
        'physicalForm',
        'quantity',
        'measure',
        'composition',
        'posology',
      ];
      fields.forEach((field) => {
        formik.setFieldTouched(field, true);
      });

      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        return;
      }

      if (!prescriptionId || !doctorId) {
        return;
      }
      try {
        if (selectedFormula && isEditingFormula) {
          const initialValues = formik.initialValues;
          const changes: Partial<Formula> = {};

          if (values.formulaName !== initialValues.formulaName) {
            changes.formulaName = values.formulaName;
          }
          if (values.prescriptionType !== initialValues.prescriptionType) {
            changes.prescriptionType = values.prescriptionType as PRESCRIPTION_TYPE;
          }
          if (values.physicalForm !== initialValues.physicalForm) {
            changes.physicalForm = values.physicalForm || '';
          }
          if (values.quantity !== initialValues.quantity) {
            changes.quantity = values.quantity as number;
          }
          if (values.measure !== initialValues.measure) {
            changes.measure = values.measure || '';
          }
          if (values.composition !== initialValues.composition) {
            changes.composition = values.composition;
          }
          if (values.posology !== initialValues.posology) {
            changes.posology = values.posology;
          }

          if (Object.keys(changes).length === 0) {
            onClose();
            return;
          }

          await FormulaService.updateFormula(prescriptionId, doctorId, selectedFormula.id, changes as any);

          const updatedPrescription = await PrescriptionService.fetchPrescriptionById(prescriptionId);
          setPrescription(updatedPrescription);

          toast.success('Fórmula atualizada com sucesso');
          props.refreshFormulas();
          props.handleSetIsEditingFormula(false);
          onClose();
        } else if (selectedFormula && !isEditingFormula) {
          const createdFromExisting = await FormulaService.createFormula(prescriptionId, doctorId, {
            formulaName: values.formulaName,
            prescriptionType: values.prescriptionType as PRESCRIPTION_TYPE,
            physicalForm: values.physicalForm || '',
            quantity: values.quantity as number,
            measure: values.measure || '',
            composition: values.composition,
            posology: values.posology,
          } as any);

          const updatedPrescription = await PrescriptionService.fetchPrescriptionById(prescriptionId);
          setPrescription(updatedPrescription);

          const hasChangesFromOriginal =
            values.formulaName !== selectedFormula.formulaName ||
            values.prescriptionType !== selectedFormula.prescriptionType ||
            values.physicalForm !== (selectedFormula.physicalForm || '') ||
            values.quantity !== selectedFormula.quantity ||
            values.measure !== (selectedFormula.measure || '') ||
            values.composition !== selectedFormula.composition ||
            values.posology !== selectedFormula.posology;

          if (hasChangesFromOriginal) {
            amplitudeModule.compoundedMedicationSavedAs({
              validation_code: prescription?.CodigoValidacao ?? '',
              partner_id: prescriberConfiguration?.idParceiro ?? 0,
              partner_name: prescriberConfiguration?.Nome ?? '',
              establishment_name: prescription?.Estabelecimento?.Nome ?? '',
              compounded_medication_id: createdFromExisting?.id ?? 0,
              name: values.formulaName,
              id_origin: selectedFormula.id ?? 0,
              name_origin: selectedFormula.formulaName ?? '',
              quantity: values.quantity as number,
              quantity_type: values.measure || '',
              posology: values.posology,
              dosage_form: values.physicalForm || '',
              units_of_measurement: values.measure || '',
              composition: !!values.composition,
              prescription_type: values.prescriptionType,
            });
          }

          toast.success('Fórmula adicionada com sucesso');
          props.refreshFormulas();
          onClose();
        } else {
          const createdFormula = await FormulaService.createFormula(prescriptionId, doctorId, {
            formulaName: values.formulaName,
            prescriptionType: values.prescriptionType as PRESCRIPTION_TYPE,
            physicalForm: values.physicalForm || '',
            quantity: values.quantity as number,
            measure: values.measure || '',
            composition: values.composition,
            posology: values.posology,
          } as any);
          const updatedPrescription = await PrescriptionService.fetchPrescriptionById(prescriptionId);
          setPrescription(updatedPrescription);

          amplitudeModule.compoundedMedicationCreated({
            validation_code: prescription?.CodigoValidacao ?? '',
            partner_id: prescriberConfiguration?.idParceiro ?? 0,
            partner_name: prescriberConfiguration?.Nome ?? '',
            establishment_name: prescription?.Estabelecimento?.Nome ?? '',
            compounded_medication_id: createdFormula?.id ?? 0,
            name: values.formulaName,
            quantity: values.quantity as number,
            quantity_type: values.measure || '',
            posology: values.posology,
            dosage_form: values.physicalForm || '',
            units_of_measurement: values.measure || '',
            composition: !!values.composition,
            prescription_type: values.prescriptionType,
          });

          toast.success('Fórmula criada com sucesso');
          props.refreshFormulas();
          onClose();
        }
      } catch (error: any) {
        formik.setSubmitting(false);

        const errorDescription = get(error, 'response.data[0].Descricao', get(error, 'response.data.Descricao', null));

        let defaultMessage = 'Erro ao processar fórmula';
        if (selectedFormula && isEditingFormula) {
          defaultMessage = 'Erro ao atualizar fórmula';
        } else if (selectedFormula && !isEditingFormula) {
          defaultMessage = 'Erro ao adicionar fórmula';
        } else {
          defaultMessage = 'Erro ao criar fórmula';
        }

        const errorMessage = errorDescription || defaultMessage;
        toast.error(errorMessage);
      }
    },
  });

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const hasValue = (value: string | number | ''): boolean => {
    if (value === '' || value === null || value === undefined) {
      return false;
    }
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    if (typeof value === 'number') {
      return !isNaN(value);
    }
    return false;
  };

  const shouldShowError = (fieldName: keyof FormulaFormValues): boolean => {
    const error = formik.errors[fieldName];
    const value = formik.values[fieldName];
    const wasTouched = formik.touched[fieldName];
    const hasBeenSubmitted = formik.submitCount > 0;

    // Se há valor válido, não mostra erro mesmo que o campo esteja touched
    if (hasValue(value)) {
      return false;
    }

    if (hasBeenSubmitted) {
      return !!error;
    }

    if (!wasTouched || !error) {
      return false;
    }

    return true;
  };

  return (
    <Flex
      position={'fixed'}
      top={'0'}
      left={'0'}
      right={'0'}
      bottom={'0'}
      justifyContent={'center'}
      alignItems={'center'}
      zIndex={1000}
      bg={'rgba(0, 0, 0, 0.5)'}
      onClick={handleOverlayClick}
    >
      <Box
        bg={'#fff'}
        borderRadius={'20px'}
        boxShadow={'0px 10.59px 31.76px 0px rgba(8, 15, 52, 0.6)'}
        p={4}
        minW={{ base: '100%', lg: '1024px' }}
        maxW={'90%'}
        maxH={{ base: '90vh', lg: 'none' }}
        onClick={(e) => e.stopPropagation()}
        display={'flex'}
        flexDirection={'column'}
      >
        <Flex flexDirection={'column'} gap={4} overflowY={{ base: 'auto', lg: 'visible' }} flex={1}>
          <Text fontSize={'16px'} fontWeight={700}>
            {selectedFormula && isEditingFormula
              ? 'Editar fórmula'
              : selectedFormula
                ? 'Adicionar fórmula'
                : 'Nova fórmula'}
          </Text>
          <Grid templateColumns={{ base: '1fr', lg: 'repeat(3, 1fr)' }} gap={4}>
            {!isEditingFormula && (
              <GridItem colSpan={{ base: 1, lg: 2 }}>
                <FormControl isInvalid={shouldShowError('formulaName')}>
                  <FormLabel>Nome</FormLabel>
                  <Input
                    placeholder={'Insira o nome da sua fórmula'}
                    value={formik.values.formulaName}
                    onChange={formik.handleChange}
                    name="formulaName"
                    onBlur={formik.handleBlur}
                  />
                  {shouldShowError('formulaName') && <FormErrorMessage>{formik.errors.formulaName}</FormErrorMessage>}
                </FormControl>
                {selectedFormula && (
                  <Text fontSize={'12px'} fontWeight={600} mt={2} color={'#1E252B'}>
                    Ao alterar o nome, uma nova fórmula será salva
                  </Text>
                )}
              </GridItem>
            )}
            <GridItem colSpan={{ base: 1, lg: isEditingFormula ? 3 : 1 }}>
              <FormControl isInvalid={shouldShowError('prescriptionType')}>
                <GenericSelect
                  options={Object.entries(MEDICINE_TYPE_OPTIONS_MAP_FORMULA).map(([key, value]) => ({
                    value: key,
                    label: value,
                  }))}
                  label="Tipo de receituário"
                  placeholder="Selecione um tipo de receituário"
                  value={formik.values.prescriptionType}
                  onChange={(val) => formik.setFieldValue('prescriptionType', val)}
                  error={shouldShowError('prescriptionType')}
                />
                {shouldShowError('prescriptionType') && (
                  <FormErrorMessage>{formik.errors.prescriptionType}</FormErrorMessage>
                )}
              </FormControl>
            </GridItem>
            <GridItem colSpan={1}>
              <FormControl isInvalid={shouldShowError('physicalForm')}>
                <LazyLoadingSelect
                  label="Forma Física"
                  placeholder="Selecione uma forma física"
                  value={formik.values.physicalForm}
                  onChange={(val) => {
                    // Limpa o erro ANTES de atualizar o valor para evitar que apareça temporariamente
                    if (val) {
                      formik.setFieldError('physicalForm', undefined);
                    }
                    // Atualiza o valor sem validar para evitar erro temporário
                    formik.setFieldValue('physicalForm', val, false);
                    // Marca como touched apenas se não houver valor válido selecionado
                    if (!val) {
                      formik.setFieldTouched('physicalForm', true, false);
                    }
                    // Se há valor válido, não marca como touched (já foi marcado no onOpen se necessário)
                    // e o erro já foi limpo acima
                  }}
                  error={shouldShowError('physicalForm')}
                  onClose={() => {
                    // Marca como touched quando a lista fecha sem seleção
                    if (!formik.values.physicalForm) {
                      formik.setFieldTouched('physicalForm', true);
                    }
                  }}
                  loadOptions={async (page, pageSize, query) => {
                    const data = await FormulaService.getPhysicalForms(page, pageSize, query);
                    return {
                      items: data.physicalForms.map((pf) => ({
                        value: pf.name,
                        label: pf.name,
                      })),
                      count: data.count,
                    };
                  }}
                />
                {shouldShowError('physicalForm') && <FormErrorMessage>{formik.errors.physicalForm}</FormErrorMessage>}
              </FormControl>
            </GridItem>
            <GridItem colSpan={1}>
              <FormControl isInvalid={shouldShowError('quantity')}>
                <FormLabel>Quantidade</FormLabel>
                <Input
                  placeholder={'Ex: 100'}
                  type="number"
                  value={formik.values.quantity}
                  onChange={formik.handleChange}
                  name="quantity"
                  onBlur={formik.handleBlur}
                />
                {shouldShowError('quantity') && <FormErrorMessage>{formik.errors.quantity}</FormErrorMessage>}
              </FormControl>
            </GridItem>
            <GridItem colSpan={1}>
              <FormControl isInvalid={shouldShowError('measure')}>
                <LazyLoadingSelect
                  label="Medida"
                  placeholder="Selecione uma medida"
                  value={formik.values.measure}
                  onChange={(val) => {
                    // Limpa o erro ANTES de atualizar o valor para evitar que apareça temporariamente
                    if (val) {
                      formik.setFieldError('measure', undefined);
                    }
                    // Atualiza o valor sem validar para evitar erro temporário
                    formik.setFieldValue('measure', val, false);
                    // Marca como touched apenas se não houver valor válido selecionado
                    if (!val) {
                      formik.setFieldTouched('measure', true, false);
                    }
                    // Se há valor válido, não marca como touched (já foi marcado no onOpen se necessário)
                    // e o erro já foi limpo acima
                  }}
                  error={shouldShowError('measure')}
                  onClose={() => {
                    // Marca como touched quando a lista fecha sem seleção
                    if (!formik.values.measure) {
                      formik.setFieldTouched('measure', true);
                    }
                  }}
                  enableLocalFilter={true}
                  loadOptions={async (page, pageSize) => {
                    const data = await FormulaService.getMeasures(page, pageSize);
                    return {
                      items: data.measures.map((measure) => ({
                        value: measure.symbol,
                        label: measure.symbol,
                      })),
                      count: data.count,
                    };
                  }}
                />
                {shouldShowError('measure') && <FormErrorMessage>{formik.errors.measure}</FormErrorMessage>}
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 1, lg: 3 }}>
              <FormControl isInvalid={shouldShowError('composition')}>
                <FormLabel>Composição</FormLabel>
                <Textarea
                  placeholder={'Insira aqui a fórmula'}
                  rows={3}
                  value={formik.values.composition}
                  onChange={formik.handleChange}
                  name="composition"
                  onBlur={formik.handleBlur}
                />
                {shouldShowError('composition') && <FormErrorMessage>{formik.errors.composition}</FormErrorMessage>}
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 1, lg: 3 }}>
              <FormControl isInvalid={shouldShowError('posology')}>
                <FormLabel>Posologia</FormLabel>
                <Input
                  placeholder={'Ex: Tomar 1 cápsula ao dia depois das refeições'}
                  value={formik.values.posology}
                  onChange={formik.handleChange}
                  name="posology"
                  onBlur={formik.handleBlur}
                />
                {shouldShowError('posology') && <FormErrorMessage>{formik.errors.posology}</FormErrorMessage>}
              </FormControl>
            </GridItem>
          </Grid>
          <Flex justifyContent={'flex-end'} gap={4} flexShrink={0} mt={{ base: 4, lg: 0 }}>
            <Button
              backgroundColor={'transparent'}
              color={'primary.main'}
              border={'2px solid'}
              borderColor={'primary.main'}
              borderRadius={'40px'}
              minW={'120px'}
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button
              backgroundColor={'primary.main'}
              color={'white'}
              borderRadius={'40px'}
              minW={'120px'}
              onClick={() => formik.handleSubmit()}
              isLoading={formik.isSubmitting}
            >
              {isEditingFormula ? 'Salvar' : isAddingFromSearch ? 'Adicionar' : 'Incluir'}
            </Button>
          </Flex>
        </Flex>
      </Box>
    </Flex>
  );
};

export default FormulaFormDialog;
